/**
 * MeetingMaterialTab: 설정 다이얼로그 > 회의자료 탭
 * v1 원본: RenewalComponents/Footer/SettingsDialog/MeetingMaterialTab
 * Redux connect → useAppSelector 전환
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { toastActions } from '@/store/slices/toastSlice';
import apiManager, { type MaterialListItem, type MaterialFileInfo } from '@/api';
import { isMasterUser } from '@/store/slices/masterSlice';
import { uploadFiles } from '@/utils/objectStorage';
import { getFileIconUrl } from '@/utils/fileIcon';
import LUXDialog from '@/components/Dialog';
import FilePreviewOverlay from './FilePreviewOverlay';
import WeDriveFinderV2, { type WeDriveFileInfo } from '@/components/DocumentShare/WeDriveFinderV2';

const RESTRICTED_EXTS = [
  'folder', 'exe', 'bat', 'cmd', 'com', 'cpl', 'js', 'jsp', 'scr', 'vbs',
  'wsf', 'ade', 'mde', 'adp', 'aspx', 'dll', 'hta', 'ins', 'iso', 'isp', 'lib',
  'lnk', 'msixbundle', 'msp', 'nsh', 'ps1', 'sct', 'wsc', 'sys', 'vb', 'vbe', 'vxd', 'wsh',
];
const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB
const MAX_FILE_COUNT = 5;
/** 문서공유 가능한 확장자 (WeDriveFinder SUPPORTED_EXTENSIONS 기준) */
const DOCUMENT_SHARE_EXTENSIONS = /^(hwp|doc|docx|rtf|xls|xlsx|cell|ppt|pptx|show|bmp|gif|jpg|jpeg|txt|one|png|pdf)$/i;

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 MB';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i];
}

interface MeetingMaterialTabProps {
  setActionBtnText: (text: string) => void;
  setActionBtnDisabled: (disabled: boolean) => void;
  onActionBtnClick: (material: MaterialListItem) => void;
  actionBtnClickRef: React.MutableRefObject<(() => void) | null>;
  documentShareMode?: boolean;
}

const MeetingMaterialTab: React.FC<MeetingMaterialTabProps> = ({
  setActionBtnText,
  setActionBtnDisabled,
  onActionBtnClick,
  actionBtnClickRef,
  documentShareMode = false,
}) => {
  const dispatch = useAppDispatch();
  const local = useAppSelector((s) => s.local);
  const masterUsers = useAppSelector((s) => s.master.masterUsers);

  const [meetingMaterials, setMeetingMaterials] = useState<MaterialListItem[]>([]);
  const [previousMaterials, setPreviousMaterials] = useState<MaterialListItem[]>([]);
  // 연결된 회의 자료 materialNo 집합 (참석 여부 무관하게 현재 목록 필터링용)
  const [linkedMaterialIds, setLinkedMaterialIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMaterials, setSelectedMaterials] = useState<number[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isTopDropdownOpen, setIsTopDropdownOpen] = useState(false);
  const [showWeDriveFinder, setShowWeDriveFinder] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const topDropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const linkedMeeting = local.linkedMeeting;
  const linkedRoomId = linkedMeeting?.roomId;

  const previousIdSet = useMemo(
    () => new Set(previousMaterials.map((m) => m.materialNo)),
    [previousMaterials],
  );
  // 이전 회의 자료(표시용)와 연결된 회의 자료(필터용)를 모두 고려하여 현재 목록에서 제외
  const linkedOrPreviousIds = useMemo(() => {
    const merged = new Set(previousIdSet);
    linkedMaterialIds.forEach((id) => merged.add(id));
    return merged;
  }, [previousIdSet, linkedMaterialIds]);
  const filteredMeetingMaterials = useMemo(
    () => meetingMaterials.filter((m) => !linkedOrPreviousIds.has(m.materialNo)),
    [meetingMaterials, linkedOrPreviousIds],
  );
  const allMaterials = useMemo(
    () => [...filteredMeetingMaterials, ...previousMaterials],
    [filteredMeetingMaterials, previousMaterials],
  );

  // ─── API 조회 ──────────────────────────────────────────────────────────

  const fetchMaterials = useCallback(async () => {
    if (!local.roomId) return;
    try {
      const list = await apiManager.getMaterialList(local.roomId);
      setMeetingMaterials(list);
    } catch {
      setMeetingMaterials([]);
    } finally {
      setIsLoading(false);
    }
  }, [local.roomId]);

  const fetchPreviousMaterials = useCallback(async () => {
    if (!linkedRoomId) {
      setPreviousMaterials([]);
      setLinkedMaterialIds(new Set());
      return;
    }

    // 1. 연결된 회의 자료 목록을 항상 가져옴 (현재 목록 필터링용)
    let linkedList: MaterialListItem[] = [];
    try {
      linkedList = await apiManager.getMaterialList(linkedRoomId);
    } catch {
      // 자료 목록 조회 실패 시 필터링 불가 — 빈 목록 유지
    }
    setLinkedMaterialIds(new Set(linkedList.map((m) => m.materialNo)));

    // 2. accessType에 따라 표시 여부 결정
    //    ALL: 모든 참석자에게 이전 회의 자료 공유 (참석 여부 체크 생략)
    //    PREVIOUS_ONLY 또는 미설정: 이전 회의 참석자에게만 공유
    if (linkedMeeting?.accessType === 'ALL') {
      setPreviousMaterials(linkedList);
      return;
    }

    // PREVIOUS_ONLY(또는 미설정): 이전 회의의 실제 참석 이력을 확인하여 표시 여부 결정
    let wasAttendee = false;
    try {
      const record = await apiManager.getRecordDetail(linkedRoomId);
      const myPortalId = local.userId;
      const myUserNo = local.userNo ? Number(local.userNo) : null;
      wasAttendee = record.attendees.some(
        (a) => (a.portalId && a.portalId === myPortalId) || (myUserNo && a.userNo === myUserNo),
      );
    } catch {
      // 조회 실패 시 비참석으로 간주
    }

    setPreviousMaterials(wasAttendee ? linkedList : []);
  }, [linkedRoomId, linkedMeeting?.accessType, local.userId, local.userNo]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  useEffect(() => {
    fetchPreviousMaterials();
  }, [fetchPreviousMaterials]);

  const handleSelectMaterial = useCallback((id: number) => {
    if (documentShareMode) {
      setSelectedMaterials((prev) => (prev.includes(id) ? [] : [id]));
    } else {
      setSelectedMaterials((prev) =>
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
      );
    }
  }, [documentShareMode]);

  const allAvailableIds = useMemo(
    () => allMaterials.map((m) => m.materialNo),
    [allMaterials],
  );

  const handleSelectAll = useCallback(() => {
    setSelectedMaterials((prev) => {
      if (prev.length === allAvailableIds.length && allAvailableIds.length > 0) {
        return [];
      }
      return [...allAvailableIds];
    });
  }, [allAvailableIds]);

  useEffect(() => {
    if (selectedMaterials.length === 1) {
      const selectedId = selectedMaterials[0];
      const selectedMat = allMaterials.find((m) => m.materialNo === selectedId);
      if (selectedMat) {
        setActionBtnText(selectedMat.fileName);
        setActionBtnDisabled(false);
        actionBtnClickRef.current = () => onActionBtnClick(selectedMat);
        return;
      }
    }
    setActionBtnText('문서 공유');
    setActionBtnDisabled(true);
    actionBtnClickRef.current = null;
  }, [selectedMaterials, allMaterials]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (topDropdownRef.current && !topDropdownRef.current.contains(e.target as Node)) {
        setIsTopDropdownOpen(false);
      }
    };
    if (isTopDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTopDropdownOpen]);

  const handleDelete = useCallback(
    (id?: number) => {
      const targets = id !== undefined ? [id] : selectedMaterials;
      if (targets.length === 0) return;

      const isMaster = isMasterUser(masterUsers, local.userId);
      const hasOthersFile = !isMaster && targets.some((materialNo) => {
        const mat = meetingMaterials.find((m) => m.materialNo === materialNo);
        return mat && mat.uploaderId !== local.userId;
      });

      const message = hasOthersFile
        ? '삭제된 파일은 복구할 수 없으며\n다른 참석자들에게 더 이상 노출되지 않습니다.\n*다른 사람이 첨부한 파일을 제외하고 삭제합니다.'
        : '삭제된 파일은 복구할 수 없으며\n다른 참석자들에게 더 이상 노출되지 않습니다.';

      LUXDialog.confirm(message, {
        title: '회의 자료를 삭제할까요?',
        type: 'question',
        confirmCallback: async () => {
          let deletedCount = 0;
          for (const materialNo of targets) {
            const mat = meetingMaterials.find((m) => m.materialNo === materialNo);
            if (hasOthersFile && mat && mat.uploaderId !== local.userId) continue;
            try {
              const res = await apiManager.deleteMaterial(materialNo);
              if (res.resultCode === 200) {
                deletedCount++;
              } else {
                console.warn(`삭제 실패(${materialNo}): ${res.resultMsg}`);
              }
            } catch (err) {
              console.error(`삭제 오류(${materialNo}):`, err);
            }
          }
          setSelectedMaterials((prev) => prev.filter((item) => !targets.includes(item)));
          await fetchMaterials();
          if (deletedCount > 0) {
            dispatch(toastActions.showSnackbar({ type: 'success', message: `회의 자료 ${deletedCount}건을 삭제하였습니다.` }));
          }
        },
      });
    },
    [selectedMaterials, meetingMaterials, local.userId, masterUsers, fetchMaterials, dispatch]
  );

  const [downloadInfo, setDownloadInfo] = useState({
    isDownloading: false,
    fileName: '',
    progress: 0,
    currentCount: 0,
    totalCount: 0,
  });
  const downloadCancelRef = useRef(false);

  const handleDownload = useCallback(
    async (id?: number) => {
      const targets = id !== undefined ? [id] : selectedMaterials;
      if (targets.length === 0) return;

      const totalCount = targets.length;
      downloadCancelRef.current = false;
      setDownloadInfo({
        isDownloading: true,
        fileName: '',
        progress: 0,
        currentCount: 0,
        totalCount: totalCount,
      });

      for (let i = 0; i < targets.length; i++) {
        if (downloadCancelRef.current) break;

        const targetId = targets[i];
        const mat = allMaterials.find((m) => m.materialNo === targetId);
        if (mat && mat.fileInfo) {
          setDownloadInfo((prev) => ({
            ...prev,
            fileName: mat.fileName,
            currentCount: i + 1,
          }));

          const baseProgress = (i / totalCount) * 100;
          const share = 100 / totalCount;

          for (let p = 0; p <= 100; p += 20) {
            if (downloadCancelRef.current) break;
            const currentTotal = Math.min(Math.round(baseProgress + (p * share / 100)), 100);
            setDownloadInfo((prev) => ({ ...prev, progress: currentTotal }));
            await new Promise((resolve) => setTimeout(resolve, 80));
          }

          if (downloadCancelRef.current) break;

          const fallbackCno = local.creatorInfo?.company_no || local.companyNo || '';
          const cno = mat.fileInfo?.cno || apiManager.selectedCno || String(fallbackCno) || undefined;

          try {
            const { downloadObjectStorageFile } = await import('@/utils/objectStorage');
            await downloadObjectStorageFile({
              FileName: mat.fileInfo.newFileName,
              BucketType: mat.fileInfo.bucketType,
              realFileName: mat.fileName,
              cno,
            });
          } catch (e) {
            console.error('Download error:', e);
            dispatch(toastActions.showSnackbar({ type: 'error', message: '파일 다운로드 중 오류가 발생했습니다.' }));
          }
        }
      }

      if (!downloadCancelRef.current) {
        dispatch(toastActions.showSnackbar({ type: 'success', message: '파일이 저장되었습니다.' }));
      }
      setDownloadInfo((prev) => ({ ...prev, isDownloading: false }));
    },
    [selectedMaterials, allMaterials, dispatch]
  );

  const handleCancelDownload = useCallback(() => {
    downloadCancelRef.current = true;
    setDownloadInfo({
      isDownloading: false,
      fileName: '',
      progress: 0,
      currentCount: 0,
      totalCount: 0,
    });
    dispatch(toastActions.showSnackbar({ type: 'success', message: '다운로드가 취소되었습니다.' }));
  }, [dispatch]);

  const [uploadInfo, setUploadInfo] = useState({
    isUploading: false,
    fileName: '',
    progress: 0,
    currentCount: 0,
    totalCount: 0,
  });

  const [previewMaterial, setPreviewMaterial] = useState<MaterialListItem | null>(null);

  const handlePreview = useCallback(
    (id: number) => {
      const mat = allMaterials.find((m) => m.materialNo === id);
      if (mat) {
        setPreviewMaterial(mat);
      }
    },
    [allMaterials]
  );

  const handleClickAttach = useCallback(() => {
    if (uploadInfo.isUploading) return;
    if (fileInputRef.current) {
      if (meetingMaterials.length >= MAX_FILE_COUNT) {
        return;
      }
      fileInputRef.current.click();
    }
  }, [meetingMaterials.length, uploadInfo.isUploading]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);
      e.target.value = '';
      if (selectedFiles.length === 0 || !local.roomId) return;

      let validFiles: File[] = [];
      const existingFileNames = new Set(filteredMeetingMaterials.map((m) => m.fileName));
      const duplicateNames: string[] = [];
      let hasRestrictedExt = false;
      let hasOversizedFile = false;

      selectedFiles.forEach((file) => {
        const extMatch = file.name.match(/\.([^.]+)$/);
        let ext = extMatch ? extMatch[1].toLowerCase() : '';
        if (!ext && file.size === 0 && file.type === '') ext = 'folder';
        if (RESTRICTED_EXTS.includes(ext)) {
          hasRestrictedExt = true;
          return;
        }
        if (file.size > MAX_FILE_SIZE) {
          hasOversizedFile = true;
          return;
        }
        if (existingFileNames.has(file.name)) {
          duplicateNames.push(file.name);
          return;
        }
        validFiles.push(file);
      });

      if (hasRestrictedExt) {
        dispatch(toastActions.enqueueSnackbar({ type: 'error', message: '업로드할 수 없는 확장자가 있습니다.' }));
      }
      if (hasOversizedFile) {
        dispatch(toastActions.enqueueSnackbar({ type: 'error', message: '최대 2GB까지 첨부할 수 있습니다.' }));
      }
      if (duplicateNames.length > 0) {
        duplicateNames.forEach((name) => {
          dispatch(toastActions.enqueueSnackbar({ type: 'error', message: `${name}파일이 이미 있습니다.` }));
        });
      }

      if (meetingMaterials.length + validFiles.length > MAX_FILE_COUNT) {
        const allowedCount = MAX_FILE_COUNT - meetingMaterials.length;
        validFiles = validFiles.slice(0, Math.max(0, allowedCount));
        dispatch(toastActions.enqueueSnackbar({ type: 'error', message: '최대 5개까지 첨부할 수 있습니다.' }));
      }

      if (validFiles.length > 0) {
        const totalCount = validFiles.length;
        setUploadInfo({
          isUploading: true,
          fileName: '',
          progress: 0,
          currentCount: 0,
          totalCount,
        });

        try {
          const materials: { fileName: string; fileInfo: MaterialFileInfo }[] = [];
          for (let i = 0; i < validFiles.length; i++) {
            const f = validFiles[i];
            setUploadInfo((prev) => ({
              ...prev,
              fileName: f.name,
              currentCount: i + 1,
            }));

            const baseProgress = (i / totalCount) * 100;
            const share = 100 / totalCount;

            for (let p = 0; p <= 100; p += 20) {
              const currentTotal = Math.min(Math.round(baseProgress + (p * share / 100)), 100);
              setUploadInfo((prev) => ({ ...prev, progress: currentTotal }));
              await new Promise((resolve) => setTimeout(resolve, 80));
            }

            const uploadResult = await uploadFiles(f);
            if (uploadResult) {
              materials.push({
                fileName: f.name,
                fileInfo: {
                  ...uploadResult,
                  cno: String(apiManager.selectedCno || local.companyNo || ''),
                },
              });
            }
          }
          if (materials.length > 0) {
            await apiManager.createMaterial(local.roomId, materials);
            await fetchMaterials();
          }
        } catch (err) {
          console.error('회의자료 등록 실패:', err);
          dispatch(toastActions.showSnackbar({ type: 'error', message: '회의 자료 업로드에 실패했습니다.' }));
        } finally {
          setUploadInfo((prev) => ({ ...prev, isUploading: false }));
        }
      }
    },
    [filteredMeetingMaterials, meetingMaterials.length, local.roomId, fetchMaterials, dispatch]
  );

  const handleWeDriveConfirm = useCallback(
    async (files: WeDriveFileInfo[]) => {
      setShowWeDriveFinder(false);
      if (!local.roomId || files.length === 0) return;

      const allowedCount = Math.max(0, MAX_FILE_COUNT - meetingMaterials.length);
      const targetFiles = files.slice(0, allowedCount);
      if (targetFiles.length === 0) return;

      const totalCount = targetFiles.length;
      setUploadInfo({
        isUploading: true,
        fileName: '',
        progress: 0,
        currentCount: 0,
        totalCount,
      });

      try {
        const materials: { fileName: string; fileInfo: MaterialFileInfo }[] = [];
        for (let i = 0; i < targetFiles.length; i++) {
          const fileInfo = targetFiles[i];
          setUploadInfo((prev) => ({
            ...prev,
            fileName: fileInfo.fileName,
            currentCount: i + 1,
          }));

          const baseProgress = (i / totalCount) * 100;
          const share = 100 / totalCount;

          for (let p = 0; p <= 100; p += 20) {
            const currentTotal = Math.min(Math.round(baseProgress + (p * share / 100)), 100);
            setUploadInfo((prev) => ({ ...prev, progress: currentTotal }));
            await new Promise((resolve) => setTimeout(resolve, 80));
          }

          materials.push({
            fileName: fileInfo.fileName,
            fileInfo: {
              newFileName: fileInfo.fileUniqueKey,
              orgFileName: fileInfo.fileName,
              thumbnail: '',
              fileSize: fileInfo.fileSize,
              bucketType: 'C',
              cno: String(apiManager.selectedCno || local.companyNo || ''),
            },
          });
        }

        await apiManager.createMaterial(local.roomId, materials);
        await fetchMaterials();
      } catch (err) {
        console.error('WE드라이브 회의자료 등록 실패:', err);
        dispatch(toastActions.showSnackbar({ type: 'error', message: '회의 자료 업로드에 실패했습니다.' }));
      } finally {
        setUploadInfo((prev) => ({ ...prev, isUploading: false }));
      }
    },
    [local.roomId, meetingMaterials.length, fetchMaterials, dispatch]
  );

  // 로딩 중
  if (isLoading) {
    return (
      <div className="rtcDialogSetupNew__meeting" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div className="rtcDialogSetupNew__meetingEmptySet">
          <p className="emptyText">회의 자료를 불러오는 중입니다...</p>
        </div>
      </div>
    );
  }

  // 회의자료 없음 상태 (현재/이전 모두 비어있는 경우)
  if (filteredMeetingMaterials.length === 0 && previousMaterials.length === 0) {
    return (
      <>
        <div className="rtcDialogSetupNew__meeting" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div className="rtcDialogSetupNew__meetingEmptySet">
            <img src="/imgs/common/ico_nodata144.svg" width="96" alt="" />
            <p className="emptyText">
              등록된 회의 자료가 없습니다.
              <br />
              필요한 자료를 업로드하여 다른 참석자와 공유해보세요!
            </p>
            {!documentShareMode && (
              <>
                <div
                  className={`emptyDropdown${isDropdownOpen ? ' open' : ''}`}
                  ref={dropdownRef}
                >
                  <button
                    type="button"
                    className="emptyDropdown__btn"
                    onClick={() => setIsDropdownOpen((prev) => !prev)}
                    disabled={uploadInfo.isUploading}
                  >
                    회의자료 첨부
                  </button>
                  <ul className="emptyDropdownList">
                    <li>
                      <button
                        type="button"
                        className="emptyDropdownList__btn selected"
                        onClick={() => {
                          handleClickAttach();
                          setIsDropdownOpen(false);
                        }}
                        disabled={uploadInfo.isUploading}
                      >
                        첨부파일 선택
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="emptyDropdownList__btn"
                        onClick={() => {
                          setShowWeDriveFinder(true);
                          setIsDropdownOpen(false);
                        }}
                        disabled={uploadInfo.isUploading}
                      >
                        WE 드라이브
                      </button>
                    </li>
                  </ul>
                  <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
                {uploadInfo.isUploading && (
                  <div className="rtcFileupload" style={{ position: 'relative', marginTop: '12px', width: '100%', maxWidth: '320px' }}>
                    <div className="rtcFileupload__top">
                      <strong className="rtcFileupload__title">
                        <span>[</span>
                        <span className="fileName ellipsis" style={{ maxWidth: '200px', display: 'inline-block', verticalAlign: 'bottom' }}>
                          {uploadInfo.fileName.lastIndexOf('.') !== -1
                            ? uploadInfo.fileName.substring(0, uploadInfo.fileName.lastIndexOf('.'))
                            : uploadInfo.fileName}
                        </span>
                        <span>
                          {uploadInfo.fileName.lastIndexOf('.') !== -1
                            ? uploadInfo.fileName.substring(uploadInfo.fileName.lastIndexOf('.'))
                            : ''}
                          ]
                        </span>
                        <span>파일을 업로드하고 있습니다.({uploadInfo.currentCount}/{uploadInfo.totalCount})</span>
                      </strong>
                    </div>
                    <div className="rtcFileupload__con">
                      <div className="rtcFileupload__lineBox">
                        <span className="rtcFileupload__line" style={{ width: `${uploadInfo.progress}%` }}></span>
                      </div>
                      <span className="perText">{uploadInfo.progress}%</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {!documentShareMode && showWeDriveFinder && (
          <WeDriveFinderV2
            onClose={() => setShowWeDriveFinder(false)}
            onConfirm={handleWeDriveConfirm}
          />
        )}
      </>
    );
  }

  const totalMaterialsCount = allMaterials.length;

  const renderMaterialItem = (
    mat: MaterialListItem,
    ext: string,
    extStr: string,
    nameWithoutExt: string,
    iconUrl: string,
    sizeStr: string,
    canDelete: boolean,
    disabled?: boolean,
  ) => (
    <li key={mat.materialNo}>
      <div className={`rtcDialogSetupNew__meetingItem${selectedMaterials.includes(mat.materialNo) ? ' selected' : ''}${disabled ? ' disabled' : ''}`}>
        <button type="button" className="selectBtn" onClick={() => !disabled && handleSelectMaterial(mat.materialNo)} disabled={disabled}>
          <span className="blind">선택</span>
        </button>
        <div className="rtcDialogSetupNew__meetingThum typeFile">
          <img
            src={iconUrl}
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://static.wehago.com/imgs/common/icon_file/120x120/ico_fn_etc_120.png';
            }}
            alt={ext}
          />
        </div>
        <div className="rtcDialogSetupNew__meetingTextBox">
          <div className="fileName">
            <p className="ellipsis">{nameWithoutExt}</p>
            <p className="ext">{extStr}</p>
          </div>
          <div className="fileInfo">
            <span className="ellipsis">{mat.uploaderName || '참석자'}</span>
            {sizeStr && (
              <>
                <span className="bar" />
                <span className="fileSize">{sizeStr}</span>
              </>
            )}
          </div>
        </div>
        <div className="rtcDialogSetupNew__hoverDimm">
          <button
            type="button"
            className="rtcDialogSetupNew__dimmBtn"
            onClick={(e) => { e.stopPropagation(); handlePreview(mat.materialNo); }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.99 3.525a.563.563 0 0 1 0 1.125H4.463c-.601 0-1.088.487-1.088 1.088v7.8c0 .6.487 1.087 1.088 1.087h7.8c.6 0 1.087-.487 1.087-1.088v-5.64a.563.563 0 0 1 1.125 0v5.64c0 1.222-.99 2.213-2.213 2.213h-7.8a2.213 2.213 0 0 1-2.212-2.213v-7.8c0-1.221.99-2.212 2.213-2.212H9.99z" fill="#fff" />
              <path d="M15.94 1.5c.033 0 .066.003.099.009l.012.002.03.009c.024.006.05.013.073.022a.562.562 0 0 1 .272.243c.012.02.024.04.033.062a.564.564 0 0 1 .043.216v4.926a.563.563 0 0 1-1.125 0V3.42l-6.54 6.54a.562.562 0 0 1-.795-.795l6.54-6.54h-3.654a.562.562 0 1 1 0-1.125h5.012z" fill="#fff" />
            </svg>
            <span className="blind">파일뷰어</span>
          </button>
          <span className="rtcDialogSetupNew__dimmbar" />
          <button
            type="button"
            className="rtcDialogSetupNew__dimmBtn ico2"
            onClick={(e) => { e.stopPropagation(); handleDownload(mat.materialNo); }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.188 10.875c.31 0 .562.252.562.563v2.1c0 1.221-.99 2.212-2.213 2.212H4.463c-1.184 0-2.15-.93-2.21-2.098l-.003-.115v-2.1a.562.562 0 1 1 1.125 0v2.1l.006.112c.056.548.519.976 1.082.976h9.074c.601 0 1.088-.487 1.088-1.088v-2.1c0-.31.252-.562.563-.562z" fill="#fff" />
              <path d="M9 2.25c.31 0 .563.252.563.563v8.017l2.04-2.04a.562.562 0 1 1 .795.795L9.45 12.532c-.011.012-.024.023-.037.033a.558.558 0 0 1-.827 0c-.012-.01-.025-.02-.036-.032L5.602 9.585a.562.562 0 1 1 .796-.795l2.04 2.04V2.812c0-.31.251-.562.562-.562z" fill="#fff" />
            </svg>
            <span className="blind">다운로드</span>
          </button>
          {canDelete && (
            <>
              <span className="rtcDialogSetupNew__dimmbar" />
              <button
                type="button"
                className="rtcDialogSetupNew__dimmBtn ico3"
                onClick={(e) => { e.stopPropagation(); handleDelete(mat.materialNo); }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 7.313c.31 0 .563.251.563.562v4.5a.562.562 0 1 1-1.126 0v-4.5c0-.31.252-.563.563-.563zM10.5 7.313c.31 0 .563.251.563.562v4.5a.562.562 0 1 1-1.126 0v-4.5c0-.31.252-.563.563-.563z" fill="#fff" />
                  <path fillRule="evenodd" clipRule="evenodd" d="M10.8 1.5c.973 0 1.762.789 1.762 1.762v.675h2.813a.562.562 0 1 1 0 1.126h-.938v9.224c0 1.222-.99 2.213-2.212 2.213h-6.45a2.213 2.213 0 0 1-2.213-2.213V5.062h-.937a.563.563 0 0 1 0-1.125h2.813v-.675c0-.973.789-1.762 1.762-1.762h3.6zM4.687 14.287c0 .601.488 1.088 1.088 1.088h6.45c.6 0 1.088-.487 1.088-1.088V5.062H4.687v9.225zM7.2 2.625a.638.638 0 0 0-.638.637v.675h4.875v-.675a.638.638 0 0 0-.637-.637H7.2z" fill="#fff" />
                </svg>
                <span className="blind">삭제</span>
              </button>
            </>
          )}
        </div>
      </div>
    </li>
  );

  return (
    <>
      <div className="rtcDialogSetupNew__meeting">
        {previewMaterial && (
          <FilePreviewOverlay
            material={previewMaterial}
            onClose={() => setPreviewMaterial(null)}
          />
        )}
        {!documentShareMode && <div className="rtcDialogSetupNew__meetingTop">
          <div className="rtcDialogSetupNew__meetingTopCheck">
            {selectedMaterials.length > 0 && selectedMaterials.length < totalMaterialsCount ? (
              <div className="common_radio_input type4 type4--dash">
                <label>
                  <input
                    className="radio_input"
                    type="checkbox"
                    checked={true}
                    onChange={handleSelectAll}
                  />
                  <span className="icon" />
                  <span className="label_text">
                    <em>{selectedMaterials.length}개</em> 선택
                  </span>
                </label>
              </div>
            ) : (
              <div className="common_radio_input type4">
                <label>
                  <input
                    className="radio_input"
                    type="checkbox"
                    checked={selectedMaterials.length > 0 && selectedMaterials.length === totalMaterialsCount}
                    onChange={handleSelectAll}
                  />
                  <span className="icon" />
                  <span className="label_text">전체선택</span>
                </label>
              </div>
            )}
          </div>
          <div className="rtcDialogSetupNew__meetingTopOpt">
            {(isMasterUser(masterUsers, local.userId) || selectedMaterials.some((id) => {
              const mat = meetingMaterials.find((m) => m.materialNo === id);
              return mat && mat.uploaderId === local.userId;
            })) && (() => {
              const hasPreviousSelected = selectedMaterials.some((id) => previousIdSet.has(id));
              const deleteDisabled = selectedMaterials.length === 0 || hasPreviousSelected;
              return (
                <button
                  type="button"
                  className={`common_round_btn sizeSmall3 notosans_font${deleteDisabled ? ' disabled2' : ''}`}
                  onClick={() => handleDelete()}
                  disabled={deleteDisabled}
                >
                  삭제
                </button>
              );
            })()}
            <button
              type="button"
              className={`common_round_btn sizeSmall3 notosans_font${selectedMaterials.length === 0 ? ' disabled2' : ''}`}
              onClick={() => handleDownload()}
              disabled={selectedMaterials.length === 0}
            >
              다운로드
            </button>
            <span className="bar" />
            <div className={`rtcDialogSetupNew__meetingTopOptDrop${isTopDropdownOpen ? ' open' : ''}`} ref={topDropdownRef}>
              <button
                type="button"
                className="common_round_btn sizeSmall3 notosans_font blue8"
                onClick={() => {
                  if (meetingMaterials.length >= MAX_FILE_COUNT) {
                    dispatch(toastActions.enqueueSnackbar({ type: 'info', message: '최대 5개까지 첨부할 수 있습니다.' }));
                    return;
                  }
                  setIsTopDropdownOpen((prev) => !prev);
                }}
                disabled={uploadInfo.isUploading}
              >
                회의자료 첨부
              </button>
              <ul className="meetingTopOptDropList">
                <li>
                  <button
                    type="button"
                    className="meetingTopOptDropList__btn selected"
                    onClick={() => {
                      handleClickAttach();
                      setIsTopDropdownOpen(false);
                    }}
                    disabled={uploadInfo.isUploading}
                  >
                    첨부파일 선택
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="meetingTopOptDropList__btn"
                    onClick={() => {
                      setShowWeDriveFinder(true);
                      setIsTopDropdownOpen(false);
                    }}
                    disabled={uploadInfo.isUploading || meetingMaterials.length >= MAX_FILE_COUNT}
                  >
                    WE 드라이브
                  </button>
                </li>
              </ul>
            </div>
            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>
        </div>}

        {downloadInfo.isDownloading && (
          <div className="rtcFiledownload" style={{ padding: '0 20px', marginBottom: '10px' }}>
            <div className="rtcFiledownload__top">
              <strong className="rtcFiledownload__title">
                <span>[</span>
                <span className="fileName ellipsis" style={{ maxWidth: '200px', display: 'inline-block', verticalAlign: 'bottom' }}>
                  {downloadInfo.fileName.lastIndexOf('.') !== -1
                    ? downloadInfo.fileName.substring(0, downloadInfo.fileName.lastIndexOf('.'))
                    : downloadInfo.fileName}
                </span>
                <span>
                  {downloadInfo.fileName.lastIndexOf('.') !== -1
                    ? downloadInfo.fileName.substring(downloadInfo.fileName.lastIndexOf('.'))
                    : ''}
                  ]
                </span>
                <span>파일을 다운로드하고 있습니다.({downloadInfo.currentCount}/{downloadInfo.totalCount})</span>
              </strong>
              <button type="button" className="rtcFiledownload__cancleBtn" onClick={handleCancelDownload}>
                <span className="lunacon lunacon--multiply-outlined-thin"></span>
              </button>
            </div>
            <div className="rtcFiledownload__con">
              <div className="rtcFiledownload__lineBox">
                <span className="rtcFiledownload__line" style={{ width: `${downloadInfo.progress}%` }}></span>
              </div>
              <span className="perText">{downloadInfo.progress}%</span>
            </div>
          </div>
        )}

        {uploadInfo.isUploading && (
          <div className="rtcFileupload" style={{ padding: '0 20px', marginBottom: '10px' }}>
            <div className="rtcFileupload__top">
              <strong className="rtcFileupload__title">
                <span>[</span>
                <span className="fileName ellipsis" style={{ maxWidth: '200px', display: 'inline-block', verticalAlign: 'bottom' }}>
                  {uploadInfo.fileName.lastIndexOf('.') !== -1
                    ? uploadInfo.fileName.substring(0, uploadInfo.fileName.lastIndexOf('.'))
                    : uploadInfo.fileName}
                </span>
                <span>
                  {uploadInfo.fileName.lastIndexOf('.') !== -1
                    ? uploadInfo.fileName.substring(uploadInfo.fileName.lastIndexOf('.'))
                    : ''}
                  ]
                </span>
                <span>파일을 업로드하고 있습니다.({uploadInfo.currentCount}/{uploadInfo.totalCount})</span>
              </strong>
            </div>
            <div className="rtcFileupload__con">
              <div className="rtcFileupload__lineBox">
                <span className="rtcFileupload__line" style={{ width: `${uploadInfo.progress}%` }}></span>
              </div>
              <span className="perText">{uploadInfo.progress}%</span>
            </div>
          </div>
        )}

        <div className="rtcDialogSetupNew__meetingListbox common_customScrollbar" style={{ flex: 1 }}>
          <ul className="rtcDialogSetupNew__meetingList">
            {filteredMeetingMaterials.map((mat) => {
              const extMatch = mat.fileName.match(/\.([^.]+)$/);
              const ext = extMatch ? extMatch[1].toLowerCase() : 'etc';
              const extStr = extMatch ? `.${extMatch[1]}` : '';
              const nameWithoutExt = extMatch ? mat.fileName.slice(0, -extMatch[0].length) : mat.fileName;
              const iconUrl = getFileIconUrl(ext, 120);
              const sizeStr = mat.fileInfo?.fileSize ? formatFileSize(mat.fileInfo.fileSize) : '';
              const canDelete = mat.uploaderId === local.userId || isMasterUser(masterUsers, local.userId);
              const shareDisabled = documentShareMode && !DOCUMENT_SHARE_EXTENSIONS.test(ext);
              return renderMaterialItem(mat, ext, extStr, nameWithoutExt, iconUrl, sizeStr, canDelete, shareDisabled);
            })}
          </ul>
          {previousMaterials.length > 0 && (
            <>
              <div className="rtcDialogSetupNew__meetingListTitle">
                <strong>이전 회의 자료</strong>
                {linkedMeeting?.name && <p>[{linkedMeeting.name}]</p>}
              </div>
              <ul className="rtcDialogSetupNew__meetingList">
                {previousMaterials.map((mat) => {
                  const extMatch = mat.fileName.match(/\.([^.]+)$/);
                  const ext = extMatch ? extMatch[1].toLowerCase() : 'etc';
                  const extStr = extMatch ? `.${extMatch[1]}` : '';
                  const nameWithoutExt = extMatch ? mat.fileName.slice(0, -extMatch[0].length) : mat.fileName;
                  const iconUrl = getFileIconUrl(ext, 120);
                  const sizeStr = mat.fileInfo?.fileSize ? formatFileSize(mat.fileInfo.fileSize) : '';
                  // 이전 회의 자료는 삭제 불가
                  const shareDisabled = documentShareMode && !DOCUMENT_SHARE_EXTENSIONS.test(ext);
                  return renderMaterialItem(mat, ext, extStr, nameWithoutExt, iconUrl, sizeStr, false, shareDisabled);
                })}
              </ul>
            </>
          )}
        </div>
      </div>
      {showWeDriveFinder && (
        <WeDriveFinderV2
          onClose={() => setShowWeDriveFinder(false)}
          onConfirm={handleWeDriveConfirm}
        />
      )}
    </>
  );
};

export default MeetingMaterialTab;
